/**
 * Scheduler - Rotation algorithm for Padel Tournament Manager
 * Implements greedy most-constrained-first algorithm with backtracking
 * Respects left/right player position constraint
 * WITH SEEDED RANDOMIZATION for reproducible results
 */

const Scheduler = {
    // Encounter matrix: encounters[i][j] = true if players i and j have met
    encounters: [],

    // Partner matrix: partners[i][j] = true if players i and j have been partners
    partners: [],

    // Opponent count matrix: opponentCounts[i][j] = number of times i and j were opponents
    opponentCounts: [],

    // Sit-out count for fair rotation (separate for left and right)
    sitOutCount: [],

    // Warnings collected during scheduling
    warnings: [],

    // Player positions cache
    playerPositions: {},

    // Fixed pairs cache
    fixedPairs: {},
    playerById: {},

    // Random number generator (will be set by seed)
    random: null,

    /**
     * Generate complete tournament schedule with seeded randomization
     * @param {Array} players - Array of player objects {id, name, position}
     * @param {number} courtCount - Number of courts
     * @param {number} roundCount - Number of rounds
     * @param {number} matchDuration - Match duration in minutes
     * @param {number} pauseDuration - Pause duration in minutes
     * @param {number} seed - Optional seed for reproducible randomization
     * @returns {Object} Schedule object with seed
     */
    generateSchedule(players, courtCount, roundCount, matchDuration, pauseDuration, seed = null) {
        const n = players.length;
        this.warnings = [];

        // Generate or use provided seed
        const generationSeed = seed !== null ? seed : Date.now();

        // Initialize seeded random generator
        this.random = this.seededRandom(generationSeed);

        // Cache player data (positions and fixed pairs)
        this.playerPositions = {};
        this.fixedPairs = {};
        this.playerById = {};

        players.forEach(p => {
            this.playerPositions[p.id] = p.position;
            this.playerById[p.id] = p;

            // Store fixed pair information
            if (p.fixedPartnerId !== null && p.fixedPartnerId !== undefined) {
                this.fixedPairs[p.id] = p.fixedPartnerId;
            }
        });

        // Separate players by position and shuffle them for randomization
        const leftPlayers = this.seededShuffle(players.filter(p => p.position === 'left'));
        const rightPlayers = this.seededShuffle(players.filter(p => p.position === 'right'));

        // Initialize encounter matrix (any encounter: partner or opponent)
        this.encounters = Array(n).fill(null).map(() => Array(n).fill(false));

        // Initialize partner matrix (only partners)
        this.partners = Array(n).fill(null).map(() => Array(n).fill(false));

        // Initialize opponent count matrix (counts how many times players were opponents)
        this.opponentCounts = Array(n).fill(null).map(() => Array(n).fill(0));

        // Initialize sit-out counter
        this.sitOutCount = Array(n).fill(0);

        // Calculate effective courts (limited by number of pairs)
        const maxCourts = Math.min(leftPlayers.length, rightPlayers.length);
        const effectiveCourts = Math.min(courtCount, Math.floor(maxCourts / 2));

        if (effectiveCourts < courtCount) {
            this.warnings.push(`Seulement ${effectiveCourts} terrain(s) utilise(s).`);
        }

        // Generate rounds
        const rounds = [];
        let currentTime = 0;
        let hasRepetitions = false;

        for (let r = 0; r < roundCount; r++) {
            const roundResult = this.generateRound(leftPlayers, rightPlayers, effectiveCourts, r);

            if (roundResult.hasRepetitions) {
                hasRepetitions = true;
            }

            const round = {
                roundIndex: r,
                startTime: currentTime,
                endTime: currentTime + matchDuration,
                matches: roundResult.matches,
                restingPlayers: roundResult.restingPlayers
            };

            rounds.push(round);
            currentTime += matchDuration + pauseDuration;
        }

        if (hasRepetitions) {
            this.warnings.push('Certaines paires de joueurs se rencontrent plusieurs fois.');
        }

        return {
            rounds,
            hasRepetitions,
            warnings: this.warnings,
            seed: generationSeed
        };
    },

    /**
     * Generate a single round
     * @param {Array} leftPlayers - All left players
     * @param {Array} rightPlayers - All right players
     * @param {number} courtCount - Number of courts
     * @param {number} roundIndex - Current round index
     * @returns {Object} Round data with matches and resting players
     */
    generateRound(leftPlayers, rightPlayers, courtCount, roundIndex) {
        // Check if we have fixed pairs
        const hasFixedPairs = Object.keys(this.fixedPairs).length > 0;

        let activeLeft, activeRight, restingPlayers;

        if (hasFixedPairs) {
            // Select complete fixed pairs to ensure they can play together
            const result = this.selectActivePlayersWithFixedPairs(leftPlayers, rightPlayers, courtCount);
            activeLeft = result.activeLeft;
            activeRight = result.activeRight;
            restingPlayers = result.restingPlayers;
        } else {
            // Original logic for no fixed pairs
            const playersPerSide = courtCount * 2; // 2 left and 2 right per court

            // Select active players (prioritize those who have rested more)
            activeLeft = this.selectActivePlayers(leftPlayers, playersPerSide);
            activeRight = this.selectActivePlayers(rightPlayers, playersPerSide);

            // Resting players
            const restingLeft = leftPlayers.filter(p => !activeLeft.find(a => a.id === p.id)).map(p => p.id);
            const restingRight = rightPlayers.filter(p => !activeRight.find(a => a.id === p.id)).map(p => p.id);
            restingPlayers = [...restingLeft, ...restingRight];
        }

        // Update sit-out count
        restingPlayers.forEach(id => {
            this.sitOutCount[id]++;
        });

        // Try to build matches respecting left/right constraint
        let result = this.buildMatches(activeLeft, activeRight, courtCount);

        if (!result.success) {
            // Fallback: allow repetitions but minimize them
            result = this.buildMatchesRelaxed(activeLeft, activeRight, courtCount);
            result.hasRepetitions = true;
        }

        // Update encounter and partner matrices
        result.matches.forEach(match => {
            this.markEncountersForMatch(match);
        });

        return {
            matches: result.matches,
            restingPlayers,
            hasRepetitions: result.hasRepetitions || false
        };
    },

    /**
     * Select active players, prioritizing those who have sat out more
     * WITH RANDOMIZATION to avoid always selecting the same combinations
     * @param {Array} players - Players of one position (left or right)
     * @param {number} count - Number of players needed
     * @returns {Array} Selected players
     */
    selectActivePlayers(players, count) {
        if (players.length <= count) {
            return this.seededShuffle([...players]);
        }

        // Group players by sit-out count
        const groups = {};
        players.forEach(p => {
            const sitOuts = this.sitOutCount[p.id];
            if (!groups[sitOuts]) groups[sitOuts] = [];
            groups[sitOuts].push(p);
        });

        // Sort groups by sit-out count (descending)
        const sortedGroups = Object.keys(groups)
            .map(Number)
            .sort((a, b) => b - a)
            .map(count => groups[count]);

        // Shuffle each group randomly, then select from highest priority
        const result = [];
        for (const group of sortedGroups) {
            const shuffled = this.seededShuffle(group);
            result.push(...shuffled);
            if (result.length >= count) break;
        }

        return result.slice(0, count);
    },

    /**
     * Select active players when fixed pairs exist
     * Selects complete pairs to ensure partners can play together
     * @param {Array} leftPlayers - All left players
     * @param {Array} rightPlayers - All right players
     * @param {number} courtCount - Number of courts
     * @returns {Object} {activeLeft, activeRight, restingPlayers}
     */
    selectActivePlayersWithFixedPairs(leftPlayers, rightPlayers, courtCount) {
        const pairsNeeded = courtCount * 2; // 2 pairs per court

        // Identify all fixed pairs
        const fixedPairsList = [];
        const processedPlayers = new Set();

        leftPlayers.forEach(leftPlayer => {
            if (processedPlayers.has(leftPlayer.id)) return;

            const rightPartnerId = this.fixedPairs[leftPlayer.id];
            if (rightPartnerId !== undefined) {
                const rightPlayer = this.playerById[rightPartnerId];
                if (rightPlayer) {
                    // Calculate pair sit-out count (sum of both players)
                    const pairSitOutCount = this.sitOutCount[leftPlayer.id] + this.sitOutCount[rightPartnerId];

                    fixedPairsList.push({
                        leftPlayer,
                        rightPlayer,
                        sitOutCount: pairSitOutCount
                    });

                    processedPlayers.add(leftPlayer.id);
                    processedPlayers.add(rightPartnerId);
                }
            }
        });

        // Also handle players without fixed pairs
        const leftWithoutPair = leftPlayers.filter(p => !processedPlayers.has(p.id));
        const rightWithoutPair = rightPlayers.filter(p => !processedPlayers.has(p.id));

        // Sort fixed pairs by sit-out count (descending - most rested first)
        fixedPairsList.sort((a, b) => b.sitOutCount - a.sitOutCount);

        // Shuffle pairs with same sit-out count for randomization
        const groupedPairs = {};
        fixedPairsList.forEach(pair => {
            if (!groupedPairs[pair.sitOutCount]) groupedPairs[pair.sitOutCount] = [];
            groupedPairs[pair.sitOutCount].push(pair);
        });

        const shuffledPairs = [];
        Object.keys(groupedPairs)
            .map(Number)
            .sort((a, b) => b - a)
            .forEach(sitOutCount => {
                const group = this.seededShuffle(groupedPairs[sitOutCount]);
                shuffledPairs.push(...group);
            });

        // Select pairs
        const selectedPairsCount = Math.min(pairsNeeded, shuffledPairs.length);
        const selectedPairs = shuffledPairs.slice(0, selectedPairsCount);

        // Extract active players from selected pairs
        const activeLeft = selectedPairs.map(pair => pair.leftPlayer);
        const activeRight = selectedPairs.map(pair => pair.rightPlayer);

        // Add non-paired players if needed to fill courts
        const remainingSlots = pairsNeeded - selectedPairsCount;
        if (remainingSlots > 0 && (leftWithoutPair.length > 0 || rightWithoutPair.length > 0)) {
            // Select additional players without pairs
            const additionalLeft = this.selectActivePlayers(leftWithoutPair, Math.min(remainingSlots, leftWithoutPair.length));
            const additionalRight = this.selectActivePlayers(rightWithoutPair, Math.min(remainingSlots, rightWithoutPair.length));

            activeLeft.push(...additionalLeft);
            activeRight.push(...additionalRight);
        }

        // Determine resting players
        const activeLeftIds = new Set(activeLeft.map(p => p.id));
        const activeRightIds = new Set(activeRight.map(p => p.id));

        const restingLeft = leftPlayers.filter(p => !activeLeftIds.has(p.id)).map(p => p.id);
        const restingRight = rightPlayers.filter(p => !activeRightIds.has(p.id)).map(p => p.id);
        const restingPlayers = [...restingLeft, ...restingRight];

        return {
            activeLeft,
            activeRight,
            restingPlayers
        };
    },

    /**
     * Build matches with left/right constraint
     * INCREASED attempts for better opponent balance
     * @param {Array} leftPlayers - Active left players
     * @param {Array} rightPlayers - Active right players
     * @param {number} courtCount - Number of courts
     * @returns {Object} {success, matches}
     */
    buildMatches(leftPlayers, rightPlayers, courtCount) {
        const maxAttempts = 300; // Optimized: 300 attempts (balance speed/quality)

        let bestResult = null;
        let bestScore = Infinity;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const result = this.tryBuildMatches(leftPlayers, rightPlayers, courtCount, true);

            if (result.success) {
                // Calculate total score for this round
                const roundScore = this.scoreRound(result.matches);

                // Keep track of the best solution found
                if (roundScore < bestScore) {
                    bestScore = roundScore;
                    bestResult = result;

                    // Early exit conditions for speed
                    if (roundScore === 0) {
                        // Perfect round (no repetitions at all)
                        return result;
                    } else if (roundScore < 200 && attempt > 50) {
                        // Good enough round found after trying a bit
                        return result;
                    }
                }
            }
        }

        return bestResult || { success: false, matches: [] };
    },

    /**
     * Try to build matches (single attempt)
     * @param {Array} leftPlayers - Active left players
     * @param {Array} rightPlayers - Active right players
     * @param {number} courtCount - Number of courts
     * @param {boolean} randomize - Whether to add randomization
     * @returns {Object} {success, matches}
     */
    tryBuildMatches(leftPlayers, rightPlayers, courtCount, randomize) {
        let availableLeft = leftPlayers.map(p => p.id);
        let availableRight = rightPlayers.map(p => p.id);

        if (randomize) {
            availableLeft = this.seededShuffle(availableLeft);
            availableRight = this.seededShuffle(availableRight);
        }

        const matches = [];

        for (let c = 0; c < courtCount; c++) {
            const match = this.buildSingleMatch(availableLeft, availableRight, randomize);

            if (!match) {
                return { success: false, matches: [] };
            }

            matches.push({
                courtIndex: c,
                team1: match.team1,
                team2: match.team2,
                result: null
            });

            // Remove assigned players
            availableLeft = availableLeft.filter(id => !match.team1.includes(id) && !match.team2.includes(id));
            availableRight = availableRight.filter(id => !match.team1.includes(id) && !match.team2.includes(id));
        }

        return { success: true, matches };
    },

    /**
     * Build a single match (2 left + 2 right players)
     * Respects fixed pairs if they exist
     * @param {Array} availableLeft - Available left player IDs
     * @param {Array} availableRight - Available right player IDs
     * @param {boolean} randomize - Whether to randomize
     * @returns {Object|null} Match object or null if failed
     */
    buildSingleMatch(availableLeft, availableRight, randomize) {
        if (availableLeft.length < 2 || availableRight.length < 2) {
            return null;
        }

        // Check if there's a fixed pair available
        const fixedPair = this.extractAvailableFixedPair(availableLeft, availableRight);

        if (fixedPair) {
            // Build match with this fixed pair
            return this.buildMatchWithFixedPair(fixedPair, availableLeft, availableRight);
        }

        // No fixed pair available, use normal algorithm
        // Sort by freedom (most constrained first)
        const sortedLeft = this.sortByFreedom(availableLeft, [...availableLeft, ...availableRight], randomize);
        const sortedRight = this.sortByFreedom(availableRight, [...availableLeft, ...availableRight], randomize);

        // Explore ALL valid combinations of 2 left + 2 right players
        // and pick the one with the LOWEST scoreMatch (minimizes opponent repetitions)
        // instead of returning the first valid match found.
        let bestMatch = null;
        let bestScore = Infinity;

        for (const left1 of sortedLeft) {
            for (const left2 of sortedLeft) {
                if (left2 === left1) continue;

                for (const right1 of sortedRight) {
                    for (const right2 of sortedRight) {
                        if (right2 === right1) continue;

                        const team1 = [left1, right1];
                        const team2 = [left2, right2];

                        if (!this.isValidMatch(team1, team2)) continue;

                        const score = this.scoreMatch(team1, team2);

                        if (score < bestScore) {
                            bestScore = score;
                            bestMatch = { team1, team2 };

                            // Early exit: perfect match (no partnership or opponent repeats)
                            if (score === 0) return bestMatch;
                        }
                    }
                }
            }
        }

        return bestMatch;
    },

    /**
     * Extract first available fixed pair from available players
     */
    extractAvailableFixedPair(availableLeft, availableRight) {
        for (const leftId of availableLeft) {
            const fixedPartnerId = this.fixedPairs[leftId];
            if (fixedPartnerId !== undefined && availableRight.includes(fixedPartnerId)) {
                return [leftId, fixedPartnerId];
            }
        }
        return null;
    },

    /**
     * Build match with a fixed pair
     * CRITICAL: Also respects fixed pairs for opponents
     */
    buildMatchWithFixedPair(fixedPair, availableLeft, availableRight) {
        const [leftId, rightId] = fixedPair;

        // Remove the fixed pair from available players
        const remainingLeft = availableLeft.filter(id => id !== leftId);
        const remainingRight = availableRight.filter(id => id !== rightId);

        if (remainingLeft.length === 0 || remainingRight.length === 0) {
            // Not enough players for a complete match
            return null;
        }

        // Find best opponents while RESPECTING fixed pairs
        let bestOpponents = null;
        let bestScore = Infinity;

        for (const oppLeft of remainingLeft) {
            for (const oppRight of remainingRight) {
                // CRITICAL: Validate that this opponent team respects fixed pairs
                const oppTeam = [oppLeft, oppRight];
                if (!this.isTeamValidForFixedPairs(oppTeam)) {
                    // This would separate a fixed pair - skip it
                    continue;
                }

                const score = this.scoreMatch([leftId, rightId], [oppLeft, oppRight]);

                if (score < bestScore) {
                    bestScore = score;
                    bestOpponents = [oppLeft, oppRight];
                }
            }
        }

        if (!bestOpponents) {
            return null;
        }

        return {
            team1: [leftId, rightId],
            team2: bestOpponents
        };
    },

    /**
     * Check if a team respects fixed pair constraints
     * Returns true if the team does NOT violate any fixed pair constraint
     */
    isTeamValidForFixedPairs(team) {
        // For each player in the team
        for (const playerId of team) {
            const fixedPartnerId = this.fixedPairs[playerId];

            // If this player has a fixed partner
            if (fixedPartnerId !== undefined) {
                // The partner MUST be in the same team
                if (!team.includes(fixedPartnerId)) {
                    return false; // Fixed pair separated - invalid
                }
            }
        }

        return true; // All constraints respected
    },

    /**
     * Check if a match is valid (no repeated partnerships)
     * MODIFIED: Only checks partnerships, not all encounters
     * Players who were opponents can become partners
     * @param {Array} team1 - [leftId, rightId]
     * @param {Array} team2 - [leftId, rightId]
     * @returns {boolean}
     */
    isValidMatch(team1, team2) {
        // Only check if team members have already been partners
        // Team 1: check if left and right have been partners before
        if (this.partners[team1[0]][team1[1]]) {
            return false;
        }

        // Team 2: check if left and right have been partners before
        if (this.partners[team2[0]][team2[1]]) {
            return false;
        }

        return true;
    },

    /**
     * Sort players by freedom (most constrained first)
     * @param {Array} playerIds - Player IDs to sort
     * @param {Array} allAvailable - All available player IDs
     * @param {boolean} randomize - Whether to randomize ties
     * @returns {Array} Sorted player IDs
     */
    sortByFreedom(playerIds, allAvailable, randomize) {
        return [...playerIds].sort((a, b) => {
            const freedomA = this.calculateFreedom(a, allAvailable);
            const freedomB = this.calculateFreedom(b, allAvailable);
            if (freedomA !== freedomB) {
                return freedomA - freedomB;
            }
            return randomize ? this.random() - 0.5 : 0;
        });
    },

    /**
     * Calculate freedom for a player (number of available partners)
     * MODIFIED: Only considers partnership history, not all encounters
     * @param {number} playerId - Player ID
     * @param {Array} availableIds - Available player IDs
     * @returns {number} Freedom count (number of players this player hasn't partnered with)
     */
    calculateFreedom(playerId, availableIds) {
        let freedom = 0;
        const playerPos = this.playerPositions[playerId];

        for (const otherId of availableIds) {
            if (otherId === playerId) continue;

            const otherPos = this.playerPositions[otherId];

            // Only count players of opposite position (left can only partner with right)
            if (playerPos !== otherPos && !this.partners[playerId][otherId]) {
                freedom++;
            }
        }
        return freedom;
    },

    /**
     * Build matches with relaxed constraints (allowing repetitions)
     * @param {Array} leftPlayers - Active left players
     * @param {Array} rightPlayers - Active right players
     * @param {number} courtCount - Number of courts
     * @returns {Object} {success, matches}
     */
    buildMatchesRelaxed(leftPlayers, rightPlayers, courtCount) {
        let availableLeft = this.seededShuffle(leftPlayers.map(p => p.id));
        let availableRight = this.seededShuffle(rightPlayers.map(p => p.id));

        const matches = [];

        for (let c = 0; c < courtCount; c++) {
            const match = this.buildSingleMatchRelaxed(availableLeft, availableRight);

            if (!match) {
                console.error('Failed to build match even with relaxed constraints');
                return { success: false, matches: [] };
            }

            matches.push({
                courtIndex: c,
                team1: match.team1,
                team2: match.team2,
                result: null
            });

            availableLeft = availableLeft.filter(id => !match.team1.includes(id) && !match.team2.includes(id));
            availableRight = availableRight.filter(id => !match.team1.includes(id) && !match.team2.includes(id));
        }

        return { success: true, matches, hasRepetitions: true };
    },

    /**
     * Build a single match minimizing repetitions
     * Respects fixed pairs if they exist
     * @param {Array} availableLeft - Available left player IDs
     * @param {Array} availableRight - Available right player IDs
     * @returns {Object|null} Best match
     */
    buildSingleMatchRelaxed(availableLeft, availableRight) {
        if (availableLeft.length < 2 || availableRight.length < 2) {
            return null;
        }

        // Check if there's a fixed pair available
        const fixedPair = this.extractAvailableFixedPair(availableLeft, availableRight);

        if (fixedPair) {
            // Build match with this fixed pair
            const fixedPairMatch = this.buildMatchWithFixedPair(fixedPair, availableLeft, availableRight);
            if (fixedPairMatch) return fixedPairMatch;
            // Fall through to relaxed algorithm if fixed pair build failed
        }

        // No fixed pair available, use relaxed algorithm
        let bestMatch = null;
        let bestScore = Infinity;

        // Try combinations and find the one with minimum repetitions
        for (let i = 0; i < availableLeft.length; i++) {
            for (let j = i + 1; j < availableLeft.length; j++) {
                for (let k = 0; k < availableRight.length; k++) {
                    for (let l = k + 1; l < availableRight.length; l++) {
                        // Try both team configurations
                        const configs = [
                            { team1: [availableLeft[i], availableRight[k]], team2: [availableLeft[j], availableRight[l]] },
                            { team1: [availableLeft[i], availableRight[l]], team2: [availableLeft[j], availableRight[k]] }
                        ];

                        for (const config of configs) {
                            // CRITICAL: Skip if this violates fixed pair constraints
                            if (!this.isTeamValidForFixedPairs(config.team1) ||
                                !this.isTeamValidForFixedPairs(config.team2)) {
                                continue;
                            }

                            const score = this.scoreMatch(config.team1, config.team2);
                            if (score < bestScore) {
                                bestScore = score;
                                bestMatch = config;
                            }
                        }
                    }
                }
            }
        }

        return bestMatch;
    },

    /**
     * Score a match (lower is better)
     * MODIFIED: Balances both partner variety AND opponent distribution
     * Uses quadratic penalty for opponent repetitions to avoid imbalance
     * @param {Array} team1 - [leftId, rightId]
     * @param {Array} team2 - [leftId, rightId]
     * @returns {number} Score (0 = perfect, higher = worse)
     */
    scoreMatch(team1, team2) {
        let score = 0;

        // CRITICAL: Block repeated partnerships (weight: 10000)
        if (this.partners[team1[0]][team1[1]]) {
            score += 10000;
        }
        if (this.partners[team2[0]][team2[1]]) {
            score += 10000;
        }

        // IMPORTANT: Heavily penalize ALL opponent repetitions
        // Reinforced penalty: any repetition costs a flat heavy price (5000),
        // plus a progressive extra cost per additional repeat.
        // This is still below the partnership block (10000) so partnership
        // repeats remain the worst offense, but a single opponent repeat is
        // now ~50x more expensive than before (was 100, now 5000).
        // Scale:
        //   0 encounters → 0
        //   1 encounter  → 5000
        //   2 encounters → 7000
        //   3 encounters → 9000
        //   4 encounters → 11000 (worse than a partnership repeat)
        const allPlayers = [...team1, ...team2];

        for (let i = 0; i < 2; i++) {
            for (let j = 2; j < 4; j++) {
                const playerId1 = allPlayers[i];
                const playerId2 = allPlayers[j];

                const encounterCount = this.opponentCounts[playerId1][playerId2];

                if (encounterCount > 0) {
                    score += 5000 + (encounterCount - 1) * 2000;
                }
            }
        }

        return score;
    },

    /**
     * Mark encounters for a match
     * @param {Object} match - Match object with team1 and team2
     */
    markEncountersForMatch(match) {
        const allPlayers = [...match.team1, ...match.team2];

        // Mark all encounters
        for (let i = 0; i < allPlayers.length; i++) {
            for (let j = i + 1; j < allPlayers.length; j++) {
                this.encounters[allPlayers[i]][allPlayers[j]] = true;
                this.encounters[allPlayers[j]][allPlayers[i]] = true;
            }
        }

        // Mark partners specifically
        this.partners[match.team1[0]][match.team1[1]] = true;
        this.partners[match.team1[1]][match.team1[0]] = true;
        this.partners[match.team2[0]][match.team2[1]] = true;
        this.partners[match.team2[1]][match.team2[0]] = true;

        // Increment opponent counts (team1 vs team2)
        for (let i = 0; i < 2; i++) {
            for (let j = 0; j < 2; j++) {
                const p1 = match.team1[i];
                const p2 = match.team2[j];
                this.opponentCounts[p1][p2]++;
                this.opponentCounts[p2][p1]++;
            }
        }
    },

    /**
     * Calculate total score for a complete round (all matches)
     * Used to compare different round configurations
     * @param {Array} matches - Array of matches
     * @returns {number} Total score (lower is better)
     */
    scoreRound(matches) {
        let totalScore = 0;
        matches.forEach(match => {
            totalScore += this.scoreMatch(match.team1, match.team2);
        });
        return totalScore;
    },

    /**
     * Seeded random number generator (simple LCG)
     * @param {number} seed - Seed value
     * @returns {Function} Random function that returns values between 0 and 1
     */
    seededRandom(seed) {
        let state = seed;
        return function() {
            state = (state * 1664525 + 1013904223) % 4294967296;
            return state / 4294967296;
        };
    },

    /**
     * Shuffle array with seed (Fisher-Yates with seeded random)
     * @param {Array} array - Array to shuffle
     * @returns {Array} Shuffled copy
     */
    seededShuffle(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(this.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    },

    /**
     * Generate multiple candidate schedules with different randomizations and return the best (async)
     * Each candidate uses a different seed for unique player ordering
     *
     * @param {Array} players - Players
     * @param {number} courtCount - Number of courts
     * @param {number} roundCount - Number of rounds
     * @param {number} matchDuration - Match duration in minutes
     * @param {number} pauseDuration - Pause duration in minutes
     * @param {number} n - Number of candidates to generate
     * @param {Function} progressCallback - Optional callback(current, total, percentage)
     * @returns {Promise<Object>} Best schedule with seed and statistics
     */
    async generateBestOfN(players, courtCount, roundCount, matchDuration, pauseDuration, n = 10, progressCallback = null) {
        console.log(`🎲 Génération de ${n} planning(s) Up&Down avec randomisation...`);

        const candidates = [];

        for (let i = 0; i < n; i++) {
            const seed = Date.now() + i * 1000; // Different seed for each candidate
            const schedule = this.generateSchedule(players, courtCount, roundCount, matchDuration, pauseDuration, seed);

            // Calculate diversity score and stats for this candidate
            let diversityScore = 0;
            let stats = null;

            if (typeof window !== 'undefined' && typeof window.TestStats !== 'undefined') {
                // Use TestStats if available (main app)
                stats = window.TestStats.analyze(players, schedule);
                diversityScore = this.calculateDiversityScoreFromStats(stats);
            } else {
                // Fallback: simple diversity calculation
                diversityScore = this.calculateDiversityScore(schedule);
            }

            candidates.push({
                schedule,
                stats,
                diversityScore,
                hasRepetitions: schedule.hasRepetitions
            });

            if (progressCallback) {
                progressCallback(i + 1, n, diversityScore);
            }

            // Small delay to ensure different timestamps
            await new Promise(resolve => setTimeout(resolve, 1));
        }

        // Sort by: 1) no repetitions first, 2) highest diversity score
        candidates.sort((a, b) => {
            if (a.hasRepetitions !== b.hasRepetitions) {
                return a.hasRepetitions ? 1 : -1;
            }
            return b.diversityScore - a.diversityScore;
        });

        const bestCandidate = candidates[0];
        const diversityScores = candidates.map(c => c.diversityScore);

        console.log(`✅ Meilleur planning sélectionné (seed: ${bestCandidate.schedule.seed})`);
        console.log(`   Diversité: ${(bestCandidate.diversityScore * 100).toFixed(1)}%`);
        console.log(`   Répétitions: ${bestCandidate.hasRepetitions ? 'Oui' : 'Non'}`);

        if (bestCandidate.stats) {
            console.log(`   Partenaires: ${(bestCandidate.stats.global.partnerDiversityScore * 100).toFixed(1)}%`);
            console.log(`   Adversaires: ${(bestCandidate.stats.global.opponentDiversityScore * 100).toFixed(1)}%`);
        }

        return {
            schedule: bestCandidate.schedule,
            stats: bestCandidate.stats,
            candidatesGenerated: n,
            bestScore: bestCandidate.diversityScore,
            bestDiversityScore: bestCandidate.diversityScore,
            avgDiversityScore: diversityScores.reduce((a, b) => a + b, 0) / n,
            minDiversityScore: Math.min(...diversityScores),
            allScores: diversityScores
        };
    },

    /**
     * Calculate diversity score from TestStats statistics
     * @param {Object} stats - Statistics from TestStats.analyze()
     * @returns {number} Combined score (0-1, higher is better)
     */
    calculateDiversityScoreFromStats(stats) {
        const partnerScore = stats.global.partnerDiversityScore;
        const opponentScore = stats.global.opponentDiversityScore;

        // Partner diversity is more important (70% weight)
        // Opponent diversity is secondary (30% weight)
        return partnerScore * 0.7 + opponentScore * 0.3;
    },

    /**
     * Calculate diversity score for a schedule (0 to 1, higher is better)
     * Based on partner and opponent distribution
     * @param {Object} schedule - Generated schedule
     * @returns {number} Diversity score
     */
    calculateDiversityScore(schedule) {
        if (!schedule.rounds || schedule.rounds.length === 0) {
            return 0;
        }

        // Count unique partnerships and opponent pairings
        const partnerships = new Set();
        const opponents = new Set();

        schedule.rounds.forEach(round => {
            round.matches.forEach(match => {
                const [p1, p2] = match.team1;
                const [p3, p4] = match.team2;

                // Partners
                partnerships.add([p1, p2].sort().join('-'));
                partnerships.add([p3, p4].sort().join('-'));

                // Opponents (all combinations)
                opponents.add([p1, p3].sort().join('-'));
                opponents.add([p1, p4].sort().join('-'));
                opponents.add([p2, p3].sort().join('-'));
                opponents.add([p2, p4].sort().join('-'));
            });
        });

        // Calculate score based on variety
        const partnerScore = partnerships.size;
        const opponentScore = opponents.size;

        // Normalize (approximate, depends on player count and rounds)
        const totalScore = partnerScore + opponentScore;
        const maxPossible = schedule.rounds.length * 8; // Rough estimate

        return Math.min(1, totalScore / maxPossible);
    },

    /**
     * Reset scheduler state
     */
    reset() {
        this.encounters = [];
        this.partners = [];
        this.opponentCounts = [];
        this.sitOutCount = [];
        this.warnings = [];
        this.playerPositions = {};
        this.fixedPairs = {};
        this.playerById = {};
        this.random = null;
    }
};

// ES6 Module Export
export default Scheduler;
